import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Ajv from 'ajv';
import { LOG_ENTRY_SCHEMA, SAVED_LOGS_SCHEMA } from '../utils/logSchema';

const ConsoleContext = createContext();

// Configurar validador Ajv
const ajv = new Ajv({ allErrors: true });
const validateLogEntry = ajv.compile(LOG_ENTRY_SCHEMA);
const validateSavedLogs = ajv.compile(SAVED_LOGS_SCHEMA);

export const ConsoleProvider = ({ children }) => {
  // Session ID único para cada sessão de edição
  const [sessionId] = useState(`session-${Date.now()}`);

  // Array de logs (ações registradas)
  const [logs, setLogs] = useState([]);

  // Contador de ordem para garantir sequência correta
  const [orderCounter, setOrderCounter] = useState(0);

  // Estado do console (aberto/fechado)
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);

  // Filtro ativo
  const [activeFilter, setActiveFilter] = useState('all');

  // Busca
  const [searchTerm, setSearchTerm] = useState('');

  // Carregar logs do localStorage ao iniciar
  useEffect(() => {
    const savedLogs = localStorage.getItem('pickiwi-console-logs');
    if (savedLogs) {
      try {
        const parsed = JSON.parse(savedLogs);

        // ✅ VALIDAR estrutura completa dos dados salvos
        const isValidStructure = validateSavedLogs(parsed);

        if (!isValidStructure) {
          console.error('❌ Logs salvos com estrutura inválida:', validateSavedLogs.errors);
          console.warn('⚠️ Limpando logs corrompidos...');
          localStorage.removeItem('pickiwi-console-logs');
          return;
        }

        if (parsed.sessionId === sessionId) {
          // ✅ VALIDAR cada log individualmente
          const validLogs = (parsed.logs || []).filter(log => {
            const isValid = validateLogEntry(log);

            if (!isValid) {
              console.warn('⚠️ Log inválido ignorado:', log);
              console.warn('Erros:', validateLogEntry.errors);
            }

            return isValid;
          });

          setLogs(validLogs);

          // Avisar se houve logs removidos
          if (validLogs.length < parsed.logs.length) {
            const removed = parsed.logs.length - validLogs.length;
            console.warn(`⚠️ ${removed} log(s) corrompido(s) removido(s)`);
          }

          // Restaurar orderCounter baseado no último log válido
          if (validLogs.length > 0) {
            const maxOrder = Math.max(...validLogs.map(log => log.order || 0));
            setOrderCounter(maxOrder + 1);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao carregar logs salvos:', error);
        console.warn('⚠️ Limpando dados corrompidos...');
        localStorage.removeItem('pickiwi-console-logs');
      }
    }
  }, [sessionId]);

  // Salvar logs no localStorage quando mudarem
  useEffect(() => {
    if (logs.length > 0) {
      localStorage.setItem('pickiwi-console-logs', JSON.stringify({
        sessionId,
        logs
      }));
    }
  }, [logs, sessionId]);

  /**
   * Adiciona uma nova ação ao log
   * @param {string} actionType - Tipo da ação (ex: 'UPLOAD_IMAGE')
   * @param {string|null} imageId - ID da imagem afetada (ou null para ações globais)
   * @param {object} details - Detalhes específicos da ação
   * @param {object} metadata - Metadados adicionais (opcional)
   */
  const addLog = useCallback((actionType, imageId, details, metadata = {}) => {
    const logEntry = {
      id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      sessionId,
      actionType,
      imageId,
      order: orderCounter, // ✅ Campo order para garantir sequência
      details: details || {},
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }
    };

    setLogs(prev => [...prev, logEntry]);
    setOrderCounter(prev => prev + 1); // Incrementar contador

    // Debug em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('🎬 Action logged:', actionType, logEntry);
    }

    return logEntry;
  }, [sessionId, orderCounter]);

  /**
   * Limpa todos os logs
   */
  const clearLogs = useCallback(() => {
    setLogs([]);
    setOrderCounter(0); // Resetar contador de ordem
    localStorage.removeItem('pickiwi-console-logs');
  }, []);

  /**
   * Exporta logs como JSON
   */
  const exportLogs = useCallback(() => {
    const exportData = {
      sessionId,
      exportedAt: Date.now(),
      totalActions: logs.length,
      logs
    };

    const data = JSON.stringify(exportData, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pickiwi-logs-${sessionId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [logs, sessionId]);

  /**
   * Copia logs selecionados para clipboard
   * @param {Array} selectedLogs - Array de logs para copiar
   */
  const copyToClipboard = useCallback((selectedLogs) => {
    const data = JSON.stringify({
      type: 'pickiwi-action-sequence',
      version: '1.0',
      actions: selectedLogs
    }, null, 2);

    navigator.clipboard.writeText(data).then(() => {
      console.log('✅ Sequência copiada para clipboard');
      return true;
    }).catch(err => {
      console.error('❌ Erro ao copiar:', err);
      return false;
    });
  }, []);

  /**
   * Cola e valida sequência do clipboard
   */
  const pasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const data = JSON.parse(text);

      // Validar formato
      if (data.type !== 'pickiwi-action-sequence') {
        throw new Error('Formato inválido');
      }

      if (!Array.isArray(data.actions)) {
        throw new Error('Array de ações inválido');
      }

      // ✅ VALIDAR cada ação (básico)
      const validActions = data.actions.filter(action => {
        if (!action.actionType) {
          console.warn('⚠️ Ação sem actionType ignorada:', action);
          return false;
        }
        return true;
      });

      if (validActions.length < data.actions.length) {
        const removed = data.actions.length - validActions.length;
        console.warn(`⚠️ ${removed} ação(ões) inválida(s) removida(s)`);
      }

      return validActions;
    } catch (error) {
      console.error('❌ Erro ao colar sequência:', error);
      return null;
    }
  }, []);

  /**
   * Remove um log específico
   */
  const removeLog = useCallback((logId) => {
    setLogs(prev => prev.filter(log => log.id !== logId));
  }, []);

  /**
   * Filtra logs por tipo
   */
  const getFilteredLogs = useCallback(() => {
    let filtered = logs;

    // Filtro por tipo
    if (activeFilter !== 'all') {
      filtered = filtered.filter(log => 
        log.actionType.toLowerCase().includes(activeFilter.toLowerCase())
      );
    }

    // Busca por termo
    if (searchTerm) {
      filtered = filtered.filter(log => {
        const searchLower = searchTerm.toLowerCase();
        return (
          log.actionType.toLowerCase().includes(searchLower) ||
          (log.details.filename && log.details.filename.toLowerCase().includes(searchLower)) ||
          (log.imageId && log.imageId.toLowerCase().includes(searchLower))
        );
      });
    }

    return filtered;
  }, [logs, activeFilter, searchTerm]);

  /**
   * Obtém estatísticas dos logs
   */
  const getStatistics = useCallback(() => {
    const stats = {
      total: logs.length,
      byType: {},
      byImage: {},
      sessionDuration: logs.length > 0 
        ? logs[logs.length - 1].timestamp - logs[0].timestamp 
        : 0
    };

    logs.forEach(log => {
      // Contar por tipo
      stats.byType[log.actionType] = (stats.byType[log.actionType] || 0) + 1;

      // Contar por imagem
      if (log.imageId) {
        stats.byImage[log.imageId] = (stats.byImage[log.imageId] || 0) + 1;
      }
    });

    return stats;
  }, [logs]);

  const value = {
    // Estado
    sessionId,
    logs,
    isConsoleOpen,
    activeFilter,
    searchTerm,

    // Ações
    addLog,
    clearLogs,
    exportLogs,
    copyToClipboard,
    pasteFromClipboard,
    removeLog,

    // UI
    setIsConsoleOpen,
    setActiveFilter,
    setSearchTerm,

    // Utilidades
    getFilteredLogs,
    getStatistics
  };

  return (
    <ConsoleContext.Provider value={value}>
      {children}
    </ConsoleContext.Provider>
  );
};

export const useConsole = () => {
  const context = useContext(ConsoleContext);
  if (!context) {
    throw new Error('useConsole must be used within a ConsoleProvider');
  }
  return context;
};

export default ConsoleContext;

